// Top picks on the home page (Oct 2026): a handful of tunes to play next,
// each card saying why (src/music/recommend.ts). It reads the same
// precomputed library file as the statistics below it and your history from
// the store, so it costs no request of its own and changes the moment you
// open, save or file a tune. A first visit shows the best known standards,
// turning over with the week.

import { libraryTasteTune, useHomeRecommendations } from '@/data/homeRecommendations';
import type { LibraryStats } from '@/music/libraryStats';
import type { PickReason } from '@/music/recommend';
import { HomeTile } from './HomeTile';
import { tuneHref } from './tuneHref';

function pickReasonText(reason: PickReason, stats: LibraryStats): string {
  switch (reason.kind) {
    case 'same-changes':
      return `Same changes as ${stats.tunes[reason.via].t}`;
    case 'similar-changes':
      return `Similar changes to ${stats.tunes[reason.via].t}`;
    case 'composer':
      return `Also by ${reason.composer}`;
  }
}

export function TopPicks() {
  const home = useHomeRecommendations();
  if (!home) return null;
  const { stats, picks } = home;
  if (picks.length === 0) return null;

  return (
    <section
      className="home-picks"
      aria-labelledby="home-picks-heading"
      data-testid="home-picks"
      data-scroll-anchor="home-picks"
    >
      <h2 id="home-picks-heading">Top picks</h2>
      <ul className="home-picks-row">
        {picks.map((pick) => {
          const tune = stats.tunes[pick.tune];
          return (
            <li key={pick.tune}>
              <HomeTile
                kind="pick"
                testId="home-pick"
                reason={pick.reason && pickReasonText(pick.reason, stats)}
                title={tune.t}
                melody={!!tune.m}
                foot={tune.c}
                to={tuneHref(tune.t, tune.c, tune.rb ? 'realbook' : undefined)}
                taste={libraryTasteTune(tune)}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
